import React from 'react'
import { Tooltip } from '@mui/material'
import {
  AddRounded, DeleteOutline, SaveOutlined, CheckCircleRounded, StarOutlineRounded, UploadFileOutlined, NoteAddOutlined,
  DescriptionOutlined, WarningAmberRounded, LockOutlined,
} from '@mui/icons-material'
import { api } from '../../function/axios'
import { ConfirmV2 } from '../ConfirmV2'
import s from './SystemV2.module.css'

/*
 * An AI assistant's settings — the support assistant and IRBI share the
 * layout, each with its own calls (CONFIG below). The classic panels
 * (page/system_page/*) are the source of every call and payload here:
 *   tools      GET / PUT /ai-assistant/tools (support only)
 *   model      the list and the current one; the support model can be
 *              changed (PUT, persist), IRBI's is set on the server and only
 *              shown, as the classic panel had it
 *   prompts    list, save (name + content, optionally made active), delete
 *   knowledge  support: files (PDF / TXT / DOC(X)) with a title and tags;
 *              IRBI: text entries (title + text)
 * A prompt saved under a new name is a new prompt (the old one stays) — the
 * classic panel worked that way, which is now said in the editor.
 */

export type AssistantKey = 'support' | 'irbi'

type Prompt = { id?: number; name: string; text: string; active: boolean; updated?: string; isNew?: boolean }
type KbRow = { id: string; title: string; tags: string[]; bytes?: number; created?: string; status: 'Indexed' | 'Processing' }

const CONFIG = {
  support: {
    tools: true,
    modelEditable: true,
    defaultPrompt: 'default',
    kb: 'files' as const,
    async loadModel() {
      const [all, cur] = await Promise.all([api({ method: 'GET', url: '/ai-assistant/models' }), api({ method: 'GET', url: '/ai-assistant/model' })])
      return { models: idsOf(all.data), current: cur.data?.model || '' }
    },
    saveModel: (model: string) => api({ method: 'PUT', url: '/ai-assistant/model', body: { model, persist: true } }),
    async loadPrompts(): Promise<Prompt[]> {
      const { data } = await api({ method: 'GET', url: '/ai-assistant/prompts' })
      return (Array.isArray(data) ? data : []).map((d: any) => ({
        id: Number(d.id), name: d.name, text: d.preview ?? '', active: !!d.active, updated: d.updated_at,
      }))
    },
    savePrompt: (p: Prompt, setActive: boolean) => api({ method: 'PUT', url: '/ai-assistant/prompt', body: { name: p.name, content: p.text, setActive } }),
    deletePrompt: (p: Prompt) => api({ method: 'DELETE', url: `/ai-assistant/prompt/${p.id}` }),
    async loadKb(): Promise<KbRow[]> {
      const { data } = await api({ method: 'GET', url: '/ai-assistant/kb' })
      return (Array.isArray(data) ? data : []).map((d: any) => ({
        id: String(d.id), title: d.title, tags: d.tags ?? [], bytes: d.bytes ?? 0, created: d.created_at, status: 'Indexed' as const,
      }))
    },
    deleteKb: (id: string) => api({ method: 'DELETE', url: `/ai-assistant/kb/${id}` }),
  },
  irbi: {
    tools: false,
    modelEditable: false,
    defaultPrompt: 'system',
    kb: 'text' as const,
    async loadModel() {
      const [cur, all] = await Promise.all([api({ method: 'GET', url: '/ai-assistant/irbi_model' }), api({ method: 'GET', url: '/ai-assistant/irbi_models' })])
      return { models: idsOf(all.data), current: cur.data?.env?.modelEnv || '' }
    },
    saveModel: (model: string) => Promise.reject(new Error(`IRBI's model (${model}) is set on the server`)),
    async loadPrompts(): Promise<Prompt[]> {
      const { data } = await api({ method: 'GET', url: '/ai-assistant/irbi_prompts' })
      return (Array.isArray(data?.items) ? data.items : []).map((d: any) => ({
        id: d.id, name: d.name, text: d.content ?? '', active: !!d.active, updated: d.updated_at,
      }))
    },
    savePrompt: (p: Prompt, setActive: boolean) => api({ method: 'POST', url: '/ai-assistant/irbi_prompt', body: { name: p.name, content: p.text, setActive } }),
    deletePrompt: (p: Prompt) => api({ method: 'DELETE', url: `/ai-assistant/irbi_prompt/${encodeURIComponent(p.name)}` }),
    async loadKb(): Promise<KbRow[]> {
      const { data } = await api({ method: 'GET', url: '/ai-assistant/irbi_kb' })
      return (Array.isArray(data) ? data : []).map((d: any) => ({
        id: String(d.id), title: d.name, tags: [], created: unixToIso(d.created_at), status: 'Indexed' as const,
      }))
    },
    deleteKb: (id: string) => api({ method: 'DELETE', url: `/ai-assistant/irbi_kb/${id}` }),
  },
}

function idsOf(data: any): string[] {
  const list: any[] = Array.isArray(data?.models) ? data.models : []
  return Array.from(new Set(list.map((m) => m.id).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b))) as string[]
}
function unixToIso(ts: unknown): string | undefined {
  const n = Number(ts)
  if (!n) return undefined
  return new Date(n < 1e12 ? n * 1000 : n).toISOString()
}
const when = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')
const size = (b?: number) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`)
const errText = (e: any) => (typeof e?.response?.data?.message === 'string' ? e.response.data.message : e?.message) || 'Something went wrong'

function Notice({ kind, text, onClose }: { kind: 'ok' | 'err'; text: string; onClose: () => void }) {
  return (
    <div className={`${s.notice} ${kind === 'err' ? s.noticeErr : s.noticeOk}`}>
      {kind === 'ok' ? <CheckCircleRounded sx={{ fontSize: 16 }} /> : <WarningAmberRounded sx={{ fontSize: 16 }} />}
      <span>{text}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss">×</button>
    </div>
  )
}

/* ── model and tools ──────────────────────────────────────────────── */

function ModelCard({ which }: { which: AssistantKey }) {
  const cfg = CONFIG[which]
  const [models, setModels] = React.useState<string[] | null>(null)
  const [current, setCurrent] = React.useState('')
  const [chosen, setChosen] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  React.useEffect(() => {
    let alive = true
    cfg.loadModel()
      .then((r) => { if (alive) { setModels(r.models); setCurrent(r.current); setChosen(r.current || r.models[0] || '') } })
      .catch((e) => { if (alive) { setModels([]); setNote({ kind: 'err', text: errText(e) }) } })
    return () => { alive = false }
  }, [cfg])

  async function save() {
    setSaving(true)
    setNote(null)
    try {
      await cfg.saveModel(chosen)
      setCurrent(chosen)
      setNote({ kind: 'ok', text: `Now answering with ${chosen}` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setSaving(false)
    }
  }

  const options = models && current && !models.includes(current) ? [current, ...models] : models || []
  return (
    <div className={s.card}>
      <div className={s.cardHead}><b>Model</b><span className={s.muted}>{cfg.modelEditable ? 'the one that writes the answers' : 'set on the server'}</span></div>
      {models === null ? <div className={s.hint}>Loading…</div> : (
        <div className={s.modelRow}>
          <select className={s.select} value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={!cfg.modelEditable || saving || !options.length}>
            {!options.length && <option value="">No models</option>}
            {options.map((m) => <option key={m} value={m}>{m}{m === current ? ' — current' : ''}</option>)}
          </select>
          {cfg.modelEditable
            ? <button type="button" className={s.primary} onClick={() => { void save() }} disabled={saving || !chosen || chosen === current}>{saving ? 'Saving…' : 'Use this model'}</button>
            : <Tooltip title="IRBI's model is chosen by the server's settings, not here"><span className={s.lockNote}><LockOutlined sx={{ fontSize: 15 }} />Read only</span></Tooltip>}
        </div>
      )}
      {note && <Notice kind={note.kind} text={note.text} onClose={() => setNote(null)} />}
    </div>
  )
}

const TOOLS: { key: 'web_search' | 'file_search' | 'remote_mcp'; title: string; desc: string }[] = [
  { key: 'web_search', title: 'Web search', desc: 'Browse the web when the knowledge base has no answer' },
  { key: 'file_search', title: 'File search', desc: 'Use the knowledge base below (IRBIS vector store)' },
  { key: 'remote_mcp', title: 'Remote MCP', desc: 'Remote tool calling — not wired by default' },
]

function ToolsCard() {
  const [tools, setTools] = React.useState<Record<string, boolean> | null>(null)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  React.useEffect(() => {
    let alive = true
    api({ method: 'GET', url: '/ai-assistant/tools' })
      .then(({ data }) => { if (alive) setTools({ web_search: !!data?.web_search, file_search: !!data?.file_search, remote_mcp: !!data?.remote_mcp }) })
      .catch((e) => { if (alive) { setTools({ web_search: false, file_search: false, remote_mcp: false }); setNote({ kind: 'err', text: errText(e) }) } })
    return () => { alive = false }
  }, [])

  async function toggle(key: string) {
    if (!tools) return
    const prev = tools
    const next = { ...tools, [key]: !tools[key] }
    setTools(next)
    setNote(null)
    try {
      await api({ method: 'PUT', url: '/ai-assistant/tools', body: { ...next, vector_store_id: null } })
    } catch (e) {
      setTools(prev)
      setNote({ kind: 'err', text: errText(e) })
    }
  }

  return (
    <div className={s.card}>
      <div className={s.cardHead}><b>Tools</b><span className={s.muted}>what the assistant may use</span></div>
      {!tools ? <div className={s.hint}>Loading…</div> : (
        <div className={s.tools}>
          {TOOLS.map((t) => (
            <div key={t.key} className={s.tool}>
              <div className={s.toolText}><b>{t.title}</b><span>{t.desc}</span></div>
              <button type="button" role="switch" aria-checked={tools[t.key]} aria-label={t.title} className={`${s.switch} ${tools[t.key] ? s.switchOn : ''}`} onClick={() => { void toggle(t.key) }}><span /></button>
            </div>
          ))}
        </div>
      )}
      {note && <Notice kind={note.kind} text={note.text} onClose={() => setNote(null)} />}
    </div>
  )
}

/* ── prompts ─────────────────────────────────────────────────────── */

function PromptsCard({ which }: { which: AssistantKey }) {
  const cfg = CONFIG[which]
  const [list, setList] = React.useState<Prompt[] | null>(null)
  const [sel, setSel] = React.useState<number>(0)
  const [draft, setDraft] = React.useState<Prompt | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [toDelete, setToDelete] = React.useState<Prompt | null>(null)
  // unsaved changes would be lost — asked first (ConfirmV2)
  const [leave, setLeave] = React.useState<{ question: string; go: () => void } | null>(null)

  const load = React.useCallback(async (keepName?: string) => {
    const items = await cfg.loadPrompts()
    // as the classic panel: with none marked, the default one is taken as active
    if (items.length && !items.some((p) => p.active)) {
      const d = items.find((p) => p.name === cfg.defaultPrompt) || items[0]
      d.active = true
    }
    setList(items)
    const i = Math.max(0, items.findIndex((p) => p.name === (keepName ?? items.find((x) => x.active)?.name)))
    setSel(i)
    setDraft(items[i] ? { ...items[i] } : null)
  }, [cfg])
  React.useEffect(() => { load().catch((e) => { setList([]); setNote({ kind: 'err', text: errText(e) }) }) }, [load])

  const original = list && !draft?.isNew ? list[sel] : null
  const dirty = !!draft && (!!draft.isNew || draft.name !== original?.name || draft.text !== original?.text)
  const renamed = !!draft && !draft.isNew && !!original && draft.name.trim() !== original.name
  const maybeShort = which === 'support' && !!original && !draft?.isNew && /(…|\.\.\.)\s*$/.test(original.text)

  function pick(i: number) {
    const go = () => {
      setSel(i)
      setDraft(list && list[i] ? { ...list[i] } : null)
      setNote(null)
    }
    if (dirty) setLeave({ question: 'Leave this prompt? Your changes are not saved.', go })
    else go()
  }
  function addNew() {
    const go = () => {
      setDraft({ name: '', text: '', active: false, isNew: true })
      setSel(-1)
      setNote(null)
    }
    if (dirty) setLeave({ question: 'Start a new prompt? Your changes are not saved.', go })
    else go()
  }

  async function save(setActive: boolean) {
    if (!draft) return
    const name = draft.name.trim()
    if (!name) { setNote({ kind: 'err', text: 'Give the prompt a name' }); return }
    if (!draft.text.trim()) { setNote({ kind: 'err', text: 'The prompt is empty' }); return }
    setSaving(true)
    setNote(null)
    try {
      await cfg.savePrompt({ ...draft, name }, setActive)
      await load(name)
      setNote({ kind: 'ok', text: setActive ? `Saved — “${name}” is the active prompt now` : `Saved “${name}”` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setSaving(false)
    }
  }

  async function remove(p: Prompt) {
    try {
      await cfg.deletePrompt(p)
      await load()
      setNote({ kind: 'ok', text: `Deleted “${p.name}”` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    }
  }

  return (
    <div className={s.card}>
      <div className={s.cardHead}>
        <b>Prompts</b><span className={s.muted}>the instructions the assistant follows — one is active</span>
        <span className={s.spacer} />
        <button type="button" className={s.ghostBtn} onClick={addNew}><AddRounded sx={{ fontSize: 16 }} />New prompt</button>
      </div>
      {list === null ? <div className={s.hint}>Loading…</div> : (
        <div className={s.prompts}>
          <div className={s.promptList}>
            {!list.length && !draft?.isNew && <div className={s.hint}>No prompts yet</div>}
            {draft?.isNew && (
              <button type="button" className={`${s.promptItem} ${s.promptOn}`}><b>{draft.name || 'New prompt'}</b><span>not saved</span></button>
            )}
            {list.map((p, i) => (
              <button key={`${p.name}-${p.id ?? i}`} type="button" className={`${s.promptItem} ${i === sel ? s.promptOn : ''}`} onClick={() => pick(i)}>
                <b>{p.name}{p.active && <em className={s.activeTag}><StarOutlineRounded sx={{ fontSize: 12 }} />active</em>}</b>
                <span>{p.updated ? `Updated ${when(p.updated)}` : ' '}</span>
              </button>
            ))}
          </div>
          {draft ? (
            <div className={s.editor}>
              <div className={s.editorTop}>
                <input className={s.input} value={draft.name} placeholder="Prompt name" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                {draft.active && !draft.isNew && <span className={s.activeBig}><StarOutlineRounded sx={{ fontSize: 15 }} />Active</span>}
              </div>
              <textarea className={s.promptText} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} placeholder="You are the support assistant of …" spellCheck={false} />
              {renamed && <div className={s.hint}>Saving under a new name creates a new prompt — “{original?.name}” stays as it is.</div>}
              {maybeShort && (
                <div className={s.warnLine}><WarningAmberRounded sx={{ fontSize: 15 }} />The list may give a shortened text (it ends with “…”). Saving it would replace the full prompt — check the end first.</div>
              )}
              <div className={s.editorBar}>
                <span className={s.muted}>{draft.text.length.toLocaleString()} characters{dirty ? ' · not saved' : ''}</span>
                <span className={s.spacer} />
                {!draft.isNew && original && (
                  <Tooltip title="Delete this prompt"><button type="button" className={s.iconDanger} onClick={() => setToDelete(original)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
                )}
                <button type="button" className={s.ghostBtn} onClick={() => { void save(false) }} disabled={saving || !dirty}><SaveOutlined sx={{ fontSize: 16 }} />Save</button>
                <button
                  type="button"
                  className={s.primary}
                  onClick={() => { void save(true) }}
                  disabled={saving || (!dirty && !!draft.active)}
                >
                  <StarOutlineRounded sx={{ fontSize: 16 }} />{dirty ? 'Save and make active' : 'Make active'}
                </button>
              </div>
            </div>
          ) : <div className={s.hint}>Choose a prompt, or start a new one</div>}
        </div>
      )}
      {note && <Notice kind={note.kind} text={note.text} onClose={() => setNote(null)} />}
      {toDelete && (
        <ConfirmV2
          header={`Delete the prompt “${toDelete.name}”?${toDelete.active ? ' It is the active one.' : ''}`}
          onYes={() => { void remove(toDelete) }}
          onClose={() => setToDelete(null)}
        />
      )}
      {leave && <ConfirmV2 header={leave.question} onYes={leave.go} onClose={() => setLeave(null)} />}
    </div>
  )
}

/* ── knowledge base ──────────────────────────────────────────────── */

function KnowledgeCard({ which }: { which: AssistantKey }) {
  const cfg = CONFIG[which]
  const [rows, setRows] = React.useState<KbRow[] | null>(null)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [toDelete, setToDelete] = React.useState<KbRow | null>(null)
  const [busy, setBusy] = React.useState(false)
  // files (support)
  const [file, setFile] = React.useState<File | null>(null)
  const [title, setTitle] = React.useState('')
  const [tags, setTags] = React.useState('')
  const [over, setOver] = React.useState(false)
  const fileRef = React.useRef<HTMLInputElement>(null)
  // text entries (IRBI)
  const [text, setText] = React.useState('')

  const load = React.useCallback(async () => {
    try { setRows(await cfg.loadKb()) } catch (e) { setRows((r) => r || []); setNote({ kind: 'err', text: errText(e) }) }
  }, [cfg])
  React.useEffect(() => { void load() }, [load])

  function pickFile(f: File | null | undefined) {
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase() || ''
    if (!['pdf', 'txt', 'doc', 'docx'].includes(ext)) { setNote({ kind: 'err', text: 'PDF, TXT, DOC or DOCX only' }); return }
    setFile(f)
    if (!title) setTitle(f.name)
    setNote(null)
  }

  async function upload() {
    if (!file) return
    setBusy(true)
    setNote(null)
    const temp: KbRow = { id: `local-${Date.now()}`, title: title || file.name, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), bytes: file.size, status: 'Processing' }
    setRows((r) => [...(r || []), temp])
    try {
      const form = new FormData()
      form.append('storage', 'openai_file_search')
      form.append('title', title || file.name)
      form.append('tags', tags || '')
      form.append('file', file)
      await api({ method: 'POST', url: '/ai-assistant/kb', body: form })
      setFile(null); setTitle(''); setTags('')
      setNote({ kind: 'ok', text: 'Uploaded — indexing has started' })
      await load()
    } catch (e) {
      setRows((r) => (r ? r.filter((x) => x.id !== temp.id) : r))
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(false)
    }
  }

  async function addText() {
    if (!title.trim() || !text.trim()) { setNote({ kind: 'err', text: 'Enter a title and the text' }); return }
    setBusy(true)
    setNote(null)
    try {
      await api({ method: 'POST', url: '/ai-assistant/irbi_kb', body: { title: title.trim(), text: text.trim() } })
      setTitle(''); setText('')
      setNote({ kind: 'ok', text: 'Added to the knowledge base' })
      await load()
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(false)
    }
  }

  async function remove(r: KbRow) {
    setRows((x) => (x ? x.filter((y) => y.id !== r.id) : x))
    try {
      await cfg.deleteKb(r.id)
      setNote({ kind: 'ok', text: `Removed “${r.title}”` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    }
    await load()
  }

  return (
    <div className={s.card}>
      <div className={s.cardHead}>
        <b>Knowledge base</b>
        <span className={s.muted}>{cfg.kb === 'files' ? 'documents the assistant can search' : 'text the assistant knows'} · {rows ? rows.length : '—'}</span>
      </div>

      {cfg.kb === 'files' ? (
        <div
          className={`${s.drop} ${over ? s.dropOver : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true) }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); pickFile(e.dataTransfer.files?.[0]) }}
        >
          {!file ? (
            <button type="button" className={s.dropBtn} onClick={() => fileRef.current?.click()}>
              <UploadFileOutlined sx={{ fontSize: 22 }} /><b>Drop a file here or choose one</b><span>PDF, TXT, DOC, DOCX</span>
            </button>
          ) : (
            <div className={s.uploadForm}>
              <span className={s.fileChip}><DescriptionOutlined sx={{ fontSize: 16 }} />{file.name} · {size(file.size)}<button type="button" onClick={() => setFile(null)} aria-label="Remove">×</button></span>
              <input className={s.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
              <input className={s.input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma-separated" />
              <button type="button" className={s.primary} onClick={() => { void upload() }} disabled={busy}><UploadFileOutlined sx={{ fontSize: 16 }} />{busy ? 'Uploading…' : 'Upload'}</button>
            </div>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.txt,.doc,.docx" hidden onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = '' }} />
        </div>
      ) : (
        <div className={s.textForm}>
          <input className={s.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
          <textarea className={s.kbText} value={text} onChange={(e) => setText(e.target.value)} placeholder="What the assistant should know…" rows={4} />
          <div className={s.editorBar}>
            <span className={s.muted}>{text.length.toLocaleString()} characters</span>
            <span className={s.spacer} />
            <button type="button" className={s.primary} onClick={() => { void addText() }} disabled={busy || !title.trim() || !text.trim()}><NoteAddOutlined sx={{ fontSize: 16 }} />{busy ? 'Adding…' : 'Add'}</button>
          </div>
        </div>
      )}

      {rows === null ? <div className={s.hint}>Loading…</div> : !rows.length ? <div className={s.hint}>Nothing yet</div> : (
        <div className={s.kbRows}>
          {rows.map((r) => (
            <div key={r.id} className={s.kbRow}>
              <DescriptionOutlined sx={{ fontSize: 18 }} className={s.kbIcon} />
              <div className={s.kbMain}>
                <b title={r.title}>{r.title}</b>
                {!!r.tags.length && <span className={s.kbTags}>{r.tags.map((t) => <i key={t}>{t}</i>)}</span>}
              </div>
              <span className={s.kbMeta}>{r.bytes != null ? size(r.bytes) : when(r.created)}</span>
              <span className={`${s.kbStatus} ${r.status === 'Processing' ? s.kbProcessing : ''}`}>{r.status}</span>
              <Tooltip title="Remove"><button type="button" className={s.iconDanger} onClick={() => setToDelete(r)} disabled={r.id.startsWith('local-')} aria-label="Remove"><DeleteOutline sx={{ fontSize: 16 }} /></button></Tooltip>
            </div>
          ))}
        </div>
      )}
      {note && <Notice kind={note.kind} text={note.text} onClose={() => setNote(null)} />}
      {toDelete && (
        <ConfirmV2 header={`Remove “${toDelete.title}” from the knowledge base?`} onYes={() => { void remove(toDelete) }} onClose={() => setToDelete(null)} />
      )}
    </div>
  )
}

export function AssistantV2({ which }: { which: AssistantKey }) {
  const cfg = CONFIG[which]
  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>{which === 'support' ? 'Support assistant' : 'IRBI assistant'}</b>
          <span>{which === 'support'
            ? 'The AI that drafts answers in the support desk.'
            : 'The AI assistant inside the dashboard.'}</span>
        </div>
      </div>
      <div className={cfg.tools ? s.topGrid : s.topGridOne}>
        <ModelCard which={which} />
        {cfg.tools && <ToolsCard />}
      </div>
      <PromptsCard which={which} />
      <KnowledgeCard which={which} />
    </div>
  )
}
